import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Wrench, 
  Phone, 
  Building2, 
  QrCode, 
  Calculator, 
  FileText, 
  Gamepad2, 
  HeartHandshake, 
  Lock, 
  Building, 
  Radio, 
  ShieldAlert, 
  Users, 
  Mail, 
  Scale, 
  Mic, 
  FileDown, 
  Search,
  Sparkles,
  ChevronRight,
  ShieldCheck,
  FileCheck2
} from 'lucide-react';

export const ToolsDirectoryPage: React.FC = () => {
  const [search, setSearch] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All');

  const tools = [
    { id: 'phone', title: 'Phone Scam & Spoof Checker', desc: 'Verify incoming mobile numbers and VoIP spoofers against ACMA robocall databases.', path: '/phone-lookup', icon: Phone, category: 'Scams' },
    { id: 'bsb', title: 'BSB & Bank Account Validator', desc: 'Verify Australian APCA bank routing and detect high-risk money-mule fintech bins.', path: '/bsb-checker', icon: Building2, category: 'Banking' },
    { id: 'abn', title: 'ABN Legitimacy & Age Checker', desc: 'Inspect company registration age and GST status on the Australian Business Register.', path: '/abn-checker', icon: Building, category: 'Business' },
    { id: 'qr', title: 'QR Code & Quishing Scanner', desc: 'Inspect decoded QR code links for parking meter scams and phishing traps.', path: '/qr-scanner', icon: QrCode, category: 'Phishing' },
    { id: 'breach_calc', title: 'Breach Exposure Risk Calculator', desc: 'Compute statistical probability of identity takeover based on exposed data classes.', path: '/breach-calculator', icon: Calculator, category: 'Identity' },
    { id: 'credit_ban', title: 'Credit Ban Declaration Generator', desc: 'Generate a legally binding Section 20HS suppression notice for Equifax, Experian & illion.', path: '/credit-ban-generator', icon: FileText, category: 'Credit' },
    { id: 'simulator', title: 'Scam Spotting Simulator Game', desc: 'Interactive training game testing your ability to spot real Australian scam messages.', path: '/scam-simulator', icon: Gamepad2, category: 'Training' },
    { id: 'digital_estate', title: 'Digital Estate & Succession Protocol', desc: 'Checklist and procedures for Australian Death Notification Service (ADNS) integration.', path: '/digital-estate', icon: HeartHandshake, category: 'Identity' },
    { id: 'pw_analyzer', title: 'Password Entropy & GPU Crack Time', desc: 'Zero-knowledge client-side entropy and RTX 4090 GPU cluster crack time calculator.', path: '/password-analyzer', icon: Lock, category: 'Password' },
    { id: 'rental_shield', title: 'Rental Application ID Packager', desc: 'Auto-watermark 100-point ID documents and redact TFNs for tenancy applications.', path: '/rental-shield', icon: Building, category: 'Documents' },
    { id: 'darkweb', title: 'Live Dark Web Breach Telemetry', desc: 'Real-time feed of monitored Australian databases leaking on dark web forums.', path: '/darkweb-feed', icon: Radio, category: 'Intelligence' },
    { id: 'threat_intel', title: 'Threat Intel & Scamwatch Digest', desc: 'Weekly cyber threat intelligence aggregated from ACMA, Scamwatch, and ACSC.', path: '/threat-intel', icon: ShieldAlert, category: 'Intelligence' },
    { id: 'family_consent', title: 'Family Privacy & Consent Manager', desc: 'APP 6-compliant digital consent workflow for monitoring adult family members.', path: '/family-consent', icon: Users, category: 'Family' },
    { id: 'email_headers', title: 'Email Header & DMARC Analyzer', desc: 'Inspect raw RFC-822 email headers for SPF, DKIM, and DMARC spoofing failures.', path: '/email-header-analyzer', icon: Mail, category: 'Phishing' },
    { id: 'oaic_complaint', title: 'OAIC Privacy Complaint Generator', desc: 'Generate Section 36 legal complaint letters to the Privacy Commissioner for compensation.', path: '/oaic-complaint-generator', icon: Scale, category: 'Legal' },
    { id: 'deepfake', title: 'AI Voice Clone Risk Assessor', desc: 'Diagnostic questionnaire evaluating synthetic voice emergency phone scams.', path: '/deepfake-audio-checker', icon: Mic, category: 'Scams' },
    { id: 'medicare', title: 'Medicare Card Compromise Audit', desc: 'Guide to rotating your Individual Reference Number (IRN) and auditing health claims.', path: '/medicare-shield', icon: HeartHandshake, category: 'Documents' },
    { id: 'insurance', title: 'Cyber Insurance Policy Comparator', desc: 'Compare Australian personal cyber insurance policies, sub-limits, and exclusions.', path: '/cyber-insurance-comparator', icon: ShieldCheck, category: 'Legal' },
    { id: 'teen', title: 'Teen Gaming & Footprint Shield', desc: 'Audit teenage gaming accounts (Discord, Roblox, Steam) and protect first TFNs.', path: '/teen-shield', icon: Gamepad2, category: 'Family' },
    { id: 'incident_export', title: 'Police Evidence Packager & PDF', desc: 'Generate an official Australian police and ReportCyber evidence dossier package.', path: '/incident-exporter', icon: FileDown, category: 'Legal' },
    { id: 'watermark', title: 'Client-Side ID Watermark Generator', desc: 'Stamp custom purpose watermarks on licences and passports directly on HTML5 canvas.', path: '/id-watermark', icon: FileCheck2, category: 'Documents' }
  ];

  const categories = ['All', 'Scams', 'Banking', 'Business', 'Phishing', 'Identity', 'Credit', 'Documents', 'Intelligence', 'Legal', 'Family', 'Password'];

  const filtered = tools.filter(t => {
    if (selectedFilter !== 'All' && t.category !== selectedFilter) return false;
    if (search.trim()) {
      const q = search.toLowerCase();
      return t.title.toLowerCase().includes(q) || t.desc.toLowerCase().includes(q) || t.category.toLowerCase().includes(q);
    }
    return true;
  });

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
      <div className="text-center space-y-3 max-w-3xl mx-auto">
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-950 text-emerald-300 text-xs font-bold border border-emerald-800">
          <Wrench className="w-3.5 h-3.5" />
          <span>AUSTRALIAN CYBER DEFENCE SUITE</span>
        </div>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white tracking-tight">
          20+ Specialized Identity Protection Utilities
        </h1>
        <p className="text-slate-400 text-sm leading-relaxed">
          Explore our complete arsenal of privacy tools: BSB verification, ABN checks, QR quishing scanners, statutory credit ban generators, and evidence packagers.
        </p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4 shadow-xl">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search security utilities (e.g. BSB, ABN, QR, Credit ban, Password, Watermark)..."
            className="w-full pl-11 pr-4 py-3 rounded-2xl bg-slate-950 border border-slate-700 text-white placeholder-slate-500 text-xs sm:text-sm focus:outline-none focus:border-emerald-500"
          />
        </div>

        <div className="flex flex-wrap gap-1.5 pt-1">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setSelectedFilter(c)}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors ${
                selectedFilter === c ? 'bg-emerald-600 text-white shadow' : 'bg-slate-950 text-slate-300 hover:text-white border border-slate-800'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map((tool) => {
          const Icon = tool.icon;
          return (
            <Link
              key={tool.id}
              to={tool.path}
              className="bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-xl flex flex-col justify-between hover:border-emerald-500/60 hover:bg-slate-900/90 transition-all group space-y-4"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-2xl bg-slate-950 border border-slate-800 flex items-center justify-center text-emerald-400 group-hover:border-emerald-500 transition-colors">
                    <Icon className="w-5 h-5" />
                  </div>
                  <span className="px-2.5 py-0.5 rounded text-[10px] bg-slate-800 text-slate-300 font-bold uppercase">
                    {tool.category}
                  </span>
                </div>

                <h3 className="text-base font-bold text-white group-hover:text-emerald-400 transition-colors">
                  {tool.title}
                </h3>

                <p className="text-xs text-slate-300 leading-relaxed">
                  {tool.desc}
                </p>
              </div>

              <div className="pt-2 text-xs font-bold text-emerald-400 flex items-center justify-between group-hover:translate-x-1 transition-transform">
                <span>Launch Tool</span>
                <ChevronRight className="w-4 h-4" />
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};
